// Changement de mot de passe.
//
// Ce que l'utilisateur doit comprendre, et que l'écran dit explicitement : sa
// clé maîtresse ne change pas. Elle est seulement ré-enveloppée. Rien n'est à
// re-chiffrer, et les parts de récupération déjà déposées restent valides.

import { useState, type FormEvent } from 'react';
import { changePassword } from './opaque';
import { useAuth } from './store';

export function ChangePassword() {
  const session = useAuth((s) => s.session);
  const masterKey = useAuth((s) => s.masterKey);
  const setExportKey = useAuth((s) => s.setExportKey);

  const [open, setOpen] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  if (!session || !masterKey) return null;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      const { exportKey, otherSessionsRevoked } = await changePassword(
        session!,
        masterKey!.masterKey,
        oldPassword,
        newPassword,
      );
      // La session courante survit, mais son exportKey a changé.
      setExportKey(exportKey);
      setOldPassword('');
      setNewPassword('');
      setDone(
        otherSessionsRevoked > 0
          ? `Mot de passe changé. ${otherSessionsRevoked} autre(s) session(s) déconnectée(s).`
          : 'Mot de passe changé.',
      );
      setOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const field =
    'mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm ' +
    'focus:border-blue-600 focus:outline-none';

  if (!open) {
    return (
      <div className="mt-4">
        {done && (
          <p data-testid="password-changed" className="mb-3 text-sm text-emerald-700">
            {done}
          </p>
        )}
        <button
          type="button"
          onClick={() => {
            setOpen(true);
            setDone(null);
          }}
          data-testid="open-change-password"
          className="w-full rounded border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          Changer mon mot de passe
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="mt-6 border-t border-slate-200 pt-6 text-left">
      <h3 className="text-sm font-semibold text-slate-900">Changer le mot de passe</h3>
      <p className="mt-1 text-xs text-slate-500">
        Ta clé maîtresse ne change pas : elle est simplement ré-enveloppée. Tes
        données restent lisibles et tes parts de récupération restent valides.
      </p>

      <label className="mt-4 block text-sm font-medium text-slate-700">
        Mot de passe actuel
        <input
          className={field}
          type="password"
          value={oldPassword}
          onChange={(e) => setOldPassword(e.target.value)}
          autoComplete="current-password"
          data-testid="old-password"
        />
      </label>

      <label className="mt-4 block text-sm font-medium text-slate-700">
        Nouveau mot de passe
        <input
          className={field}
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          autoComplete="new-password"
          data-testid="new-password"
        />
      </label>

      {error && (
        <p data-testid="change-password-error" className="mt-4 text-sm text-red-600">
          {error}
        </p>
      )}

      <div className="mt-6 flex gap-3">
        <button
          type="submit"
          disabled={busy}
          data-testid="submit-change-password"
          className="flex-1 rounded bg-blue-900 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800 disabled:opacity-50"
        >
          {busy ? 'Changement…' : 'Valider'}
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="rounded border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
        >
          Annuler
        </button>
      </div>
    </form>
  );
}
